import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sea-snag/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Grid3X3,
  Loader2,
  Pencil,
  Pin,
  Settings2,
  Ship,
  Trash2,
} from 'lucide-react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { BOAT_KINDS, formatCurrency, formatWeekRange } from '../../components/comp-set-shared';
import { CompSetSummaryStrip } from '../../components/comp-set-summary-strip';
import { CompSetWizardDialog } from '../../components/comp-set-wizard';
import { ConfirmDialog } from '../../components/confirm-dialog';
import {
  type EquipmentComparable,
  EquipmentScoreBadge,
} from '../../components/equipment-score-badge';
import { LoadingState } from '../../components/loading-state';
import { MarketStatusCard } from '../../components/market-status-card';
import { PositionDistributionChart } from '../../components/position-distribution-chart';
import { WeekRankTable } from '../../components/week-rank-table';
import { PositionStripLegend, WeeklyPositionStrip } from '../../components/weekly-position-strip';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import { useYear } from '../../hooks/use-year';
import {
  type CalendarWeek,
  type CompSet,
  compSetsApi,
  type EquipmentItem,
  type SideBySideBoatRow,
  type SideBySideWeekPrice,
  type WeeklyComparisonBoat,
} from '../../lib/api-client';
import { getRolloverTarget, getYearSwitchTarget } from '../../lib/comp-set-year';
import { buildEquipmentDiff, formatPoints, sumPoints } from '../../lib/equipment-score';
import { maxMToFt, minMToFt } from '../../lib/length-filter';
import { queryKeys } from '../../lib/query-keys';
import { isSelectableYear, setYear } from '../../stores/year-store';

// `weekStart` deep-links the Price Comparison view to a specific week
// (issue #126). Price-alert notifications navigate here with the alert's
// dateFrom so the user lands on the same week the change happened.
const compSetSearchSchema = z.object({
  weekStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'weekStart must be YYYY-MM-DD')
    .optional(),
});

export const Route = createFileRoute('/$orgPid/competitor-sets/$pid')({
  component: CompetitorSetDetailPage,
  validateSearch: compSetSearchSchema,
});

function CompetitorSetDetailPage() {
  const { organization, isLoading } = useRequiredOrganization();
  const { pid } = Route.useParams();

  if (isLoading) return <LoadingState />;

  return <DetailContent orgPid={organization.pid} pid={pid} />;
}

function DetailContent({ orgPid, pid }: { orgPid: string; pid: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);

  // The year comes from the header control, shared with every other screen.
  const year = useYear();
  // A deep-linked week — say a price alert for a next-season week — still wins
  // on arrival, so the calendar actually contains the week being linked to.
  const linkedYear = search.weekStart ? Number(search.weekStart.slice(0, 4)) : null;
  // One-shot: the first normalization run against a loaded calendar is the
  // initial landing — only that run may fire the stale-deep-link toast.
  // Marked even when the landing weekStart is valid, so later in-app or
  // hand-edited URLs never toast.
  const initialLandingEvaluatedRef = useRef(false);

  const { data: compSet, isLoading } = useQuery({
    queryKey: queryKeys.compSets.detail(pid),
    queryFn: () => compSetsApi.get(pid),
  });

  const boatPid = compSet?.referenceBoats[0]?.id ?? '';

  // Calendar data (feeds summary, charts, position strip)
  const { data: calendarData, isLoading: calendarLoading } = useQuery({
    queryKey: queryKeys.compSets.calendar(pid, year, boatPid),
    queryFn: () =>
      compSetsApi.calendar(pid, {
        year,
        ...(boatPid && { boatPid }),
      }),
    enabled: !!compSet,
  });

  const calendar = calendarData ?? [];

  // Selected week is DERIVED from the URL — `weekStart` is the single source
  // of truth for both the active year and the active week. All selection
  // changes go through selectWeek(); there is no separate selection state to
  // fall out of sync when the calendar swaps years.
  const selectedWeekIndex = useMemo(() => {
    if (!search.weekStart || calendar.length === 0) return null;
    const idx = calendar.findIndex((w) => w.weekStart === search.weekStart);
    return idx >= 0 ? idx : null;
  }, [calendar, search.weekStart]);

  const selectWeek = useCallback(
    (weekStart: string, opts?: { push?: boolean }) => {
      void navigate({
        to: '/$orgPid/competitor-sets/$pid',
        params: { orgPid, pid },
        search: { weekStart },
        // Week scrubbing replaces history to avoid polluting it; year jumps
        // push so the browser Back button returns to the previous year/week.
        replace: !opts?.push,
      });
    },
    [navigate, orgPid, pid]
  );

  // Deep link wins once; after that the header owns the year and switching it
  // jumps to the equivalent week, exactly as the page's own year select did.
  const appliedLinkedYear = useRef(false);
  useEffect(() => {
    if (appliedLinkedYear.current) return;
    appliedLinkedYear.current = true;
    if (linkedYear !== null && isSelectableYear(linkedYear) && linkedYear !== year) {
      setYear(linkedYear);
    }
  }, [linkedYear, year]);

  useEffect(() => {
    if (!appliedLinkedYear.current) return;
    if (linkedYear === null || linkedYear === year) return;
    selectWeek(getYearSwitchTarget(year, new Date()), { push: true });
  }, [year, linkedYear, selectWeek]);

  // URL normalization: once a calendar is loaded, ensure the URL names a week
  // that exists in it. Covers the first landing (no weekStart), stale
  // deep-links (toast once, issue #126), and hand-edited URLs.
  useEffect(() => {
    if (calendar.length === 0) return;
    const isInitialLanding = !initialLandingEvaluatedRef.current;
    initialLandingEvaluatedRef.current = true;
    if (search.weekStart && calendar.some((w) => w.weekStart === search.weekStart)) return;

    if (search.weekStart && isInitialLanding) {
      toast.info('That week is no longer in range — showing the current week instead.');
    }

    const today = new Date().toISOString().split('T')[0]!;
    const fallbackIdx = calendar.findIndex((w) => w.weekStart >= today);
    const week = calendar[fallbackIdx >= 0 ? fallbackIdx : 0]!;
    selectWeek(week.weekStart);
  }, [calendar, search.weekStart, selectWeek]);

  const deleteMutation = useMutation({
    mutationFn: () => compSetsApi.remove(pid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.list(orgPid) });
      toast.success('Competitor set deleted');
      void navigate({ to: '/$orgPid/competitor-sets', params: { orgPid } });
    },
    onError: () => toast.error('Failed to delete competitor set'),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-6 w-96" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
        <Skeleton className="h-[350px] w-full" />
      </div>
    );
  }

  if (!compSet) {
    return (
      <div className="space-y-4">
        <Link
          to="/$orgPid/competitor-sets"
          params={{ orgPid }}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 inline size-4" />
          Back to Competitor Sets
        </Link>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Competitor set not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section 1: Header */}
      <PageHeader
        compSet={compSet}
        orgPid={orgPid}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDeleteOpen(true)}
      />

      {/* Sections 3-5: Calendar data sections */}
      {calendarLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-[350px] w-full" />
        </div>
      ) : calendar.length > 0 ? (
        <>
          {/* Sections 3-4: Summary + Position Strip | Position Distribution */}
          {boatPid && (
            <div className="flex flex-col gap-6 lg:flex-row">
              {/* Left 70%: Summary cards + Year at a Glance */}
              <div className="flex w-full flex-col gap-6 lg:w-[70%]">
                <CompSetSummaryStrip
                  week={selectedWeekIndex !== null ? (calendar[selectedWeekIndex] ?? null) : null}
                  weekLabel={
                    selectedWeekIndex !== null
                      ? formatWeekRange(
                          calendar[selectedWeekIndex]?.weekStart ?? '',
                          calendar[selectedWeekIndex]?.weekEnd ?? ''
                        )
                      : ''
                  }
                  onOpenMatrix={() => setMatrixOpen(true)}
                />
                {selectedWeekIndex !== null && (
                  <MarketStatusCard
                    calendar={calendar}
                    selectedWeekIndex={selectedWeekIndex}
                    weekLabel={formatWeekRange(
                      calendar[selectedWeekIndex]?.weekStart ?? '',
                      calendar[selectedWeekIndex]?.weekEnd ?? ''
                    )}
                  />
                )}
                <Card className="flex flex-1 flex-col">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Year at a Glance: {year}</CardTitle>
                      <div className="flex items-center gap-3">
                        <PositionStripLegend />
                      </div>
                    </div>
                    <CardDescription>
                      Click a week to see detailed price comparison below
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <WeeklyPositionStrip
                      data={calendar}
                      onWeekClick={(idx) => {
                        const w = calendar[idx];
                        if (w) selectWeek(w.weekStart);
                      }}
                      selectedIndex={selectedWeekIndex ?? undefined}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Right: Position Distribution (desktop only) + Week Rank (all sizes) */}
              <div className="flex w-full flex-col gap-6 lg:w-[30%]">
                <Card className="hidden h-full lg:block">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Position Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PositionDistributionChart data={calendar} />
                  </CardContent>
                </Card>
                {selectedWeekIndex !== null && calendar[selectedWeekIndex] && (
                  <WeekRankTable
                    compSetId={compSet.id}
                    weekStart={calendar[selectedWeekIndex]!.weekStart}
                    weekLabel={formatWeekRange(
                      calendar[selectedWeekIndex]!.weekStart,
                      calendar[selectedWeekIndex]!.weekEnd
                    )}
                  />
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground text-sm">
              No calendar data available. Ensure pricing data exists for your boat.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Section 6: Selected Week Price Comparison */}
      {calendar.length > 0 && selectedWeekIndex !== null && (
        <WeekPriceComparison
          compSet={compSet}
          calendar={calendar}
          selectedWeekIndex={selectedWeekIndex}
          year={year}
          onSelectWeek={selectWeek}
        />
      )}

      {/* Section 7: Full Season Matrix dialog (also triggered from summary strip) */}
      <CollapsibleSideBySide
        compSet={compSet}
        year={year}
        open={matrixOpen}
        onOpenChange={setMatrixOpen}
      />

      {/* Dialogs */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete Competitor Set"
        description={`Are you sure you want to delete "${compSet.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => deleteMutation.mutate()}
      />

      <CompSetWizardDialog
        mode="edit"
        orgPid={orgPid}
        initialData={compSet}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </div>
  );
}

// ── Page Header ──

function PageHeader({
  compSet,
  orgPid,
  onEdit,
  onDelete,
}: {
  compSet: CompSet;
  orgPid: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="space-y-3">
      <Link
        to="/$orgPid/competitor-sets"
        params={{ orgPid }}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="mr-1 size-4" />
        Back to Competitor Sets
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">{compSet.name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            {compSet.referenceBoats.length > 0 && (
              <Badge variant="secondary" className="gap-1">
                <Ship className="size-3" />
                {compSet.referenceBoats.map((b) => b.name).join(', ')}
              </Badge>
            )}
            {compSet.filterKind && (
              <Badge variant="outline" className="text-xs">
                {BOAT_KINDS[compSet.filterKind] ?? compSet.filterKind}
              </Badge>
            )}
            {(compSet.filterMinLength != null || compSet.filterMaxLength != null) && (
              <Badge variant="outline" className="text-xs">
                {`${compSet.filterMinLength != null ? minMToFt(Number(compSet.filterMinLength)) : '?'}–${
                  compSet.filterMaxLength != null ? maxMToFt(Number(compSet.filterMaxLength)) : '?'
                } ft`}
              </Badge>
            )}
            {compSet.filterHomeBases.length > 0 && (
              <Badge variant="outline" className="text-xs">
                {compSet.filterHomeBases.length === 1
                  ? compSet.filterHomeBases[0]
                  : `${compSet.filterHomeBases.length} bases`}
              </Badge>
            )}
            <Badge
              variant={
                compSet.status === 'syncing'
                  ? 'secondary'
                  : compSet.status === 'failed'
                    ? 'destructive'
                    : 'secondary'
              }
              className={compSet.status === 'syncing' ? 'animate-pulse' : ''}
            >
              {compSet.status === 'syncing' ? (
                <>
                  <Loader2 className="mr-1 size-3 animate-spin" />
                  Syncing...
                </>
              ) : compSet.status === 'failed' ? (
                'Sync failed'
              ) : (
                `${compSet.marketBoatCount} ${compSet.marketBoatCount === 1 ? 'competitor' : 'competitors'}`
              )}
            </Badge>
            {compSet.smartSyncEnabled && (
              <Badge variant="outline" className="text-xs">
                Smart Sync
              </Badge>
            )}
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1 size-3.5" />
            Edit
          </Button>
          <Button variant="outline" size="sm" className="text-destructive" onClick={onDelete}>
            <Trash2 className="mr-1 size-3.5" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Selected Week Price Comparison ──

type SortMode = 'price' | 'model';

// Compact year dropdown shared by the Year at a Glance header and the Price

function WeekPriceComparison({
  compSet,
  calendar,
  selectedWeekIndex,
  year,
  onSelectWeek,
}: {
  compSet: CompSet;
  calendar: CalendarWeek[];
  selectedWeekIndex: number;
  year: number;
  onSelectWeek: (weekStart: string, opts?: { push?: boolean }) => void;
}) {
  const queryClient = useQueryClient();
  const [sortMode, setSortMode] = useState<SortMode>('price');
  const [source, setSource] = useState<'mmk' | 'boataround'>('mmk');
  const [adjustedDiscounts, setAdjustedDiscounts] = useState<Record<string, number>>({});

  const selectedWeek = calendar[selectedWeekIndex]!;
  const weekStart = selectedWeek.weekStart;
  const weekLabel = formatWeekRange(selectedWeek.weekStart, selectedWeek.weekEnd);

  // Cross-year rollover: null when not at a boundary or the target year is
  // outside the offered horizon (spec §3).
  const prevRollover = getRolloverTarget(
    'prev',
    year,
    selectedWeekIndex,
    calendar.length,
    new Date()
  );
  const nextRollover = getRolloverTarget(
    'next',
    year,
    selectedWeekIndex,
    calendar.length,
    new Date()
  );

  // Reset discounts when week changes
  const prevWeekRef = useRef(weekStart);
  useEffect(() => {
    if (prevWeekRef.current !== weekStart) {
      setAdjustedDiscounts({});
      prevWeekRef.current = weekStart;
    }
  }, [weekStart]);

  const {
    data: boats,
    isLoading,
    isPlaceholderData,
  } = useQuery({
    queryKey: queryKeys.compSets.weeklyComparison(compSet.id, weekStart, source),
    queryFn: () => compSetsApi.weeklyComparison(compSet.id, { weekStart, source }),
    placeholderData: keepPreviousData,
  });

  const discountMutation = useMutation({
    mutationFn: ({
      boatPid,
      discountPct,
      product,
    }: {
      boatPid: string;
      discountPct: number;
      product?: string;
    }) =>
      compSetsApi.updateDiscount(compSet.id, boatPid, {
        discountPct,
        weekStart,
        pushToProvider: true,
        ...(product && { product }),
      }),
    onMutate: ({ boatPid, discountPct }) => {
      const queryKey = queryKeys.compSets.weeklyComparison(compSet.id, weekStart, source);
      const previous = queryClient.getQueryData<WeeklyComparisonBoat[]>(queryKey);

      if (previous) {
        queryClient.setQueryData<WeeklyComparisonBoat[]>(queryKey, (old) =>
          old?.map((boat) => {
            if (boat.id !== boatPid || boat.listPrice == null) return boat;
            const newPrice = Math.round(boat.listPrice * (1 - discountPct / 100) * 100) / 100;
            return { ...boat, discountPct, priceAfterDiscount: newPrice };
          })
        );
      }

      setAdjustedDiscounts((prev) => {
        const next = { ...prev };
        delete next[boatPid];
        return next;
      });

      return { previous };
    },
    onSuccess: (result) => {
      toast.success(
        `Discount updated to ${result.discountPct}%.${result.pushedToProvider ? ' Pushed to MMK.' : ''}`
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.compSets.weeklyComparisonAll(compSet.id),
      });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        const queryKey = queryKeys.compSets.weeklyComparison(compSet.id, weekStart, source);
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error('Failed to update discount');
    },
  });

  const getEffectivePrice = useCallback(
    (boat: WeeklyComparisonBoat): number | null => {
      const adj = adjustedDiscounts[boat.id];
      if (adj !== undefined && boat.listPrice != null) {
        return Math.round(boat.listPrice * (1 - adj / 100));
      }
      return boat.priceAfterDiscount;
    },
    [adjustedDiscounts]
  );

  const allEquipment = useMemo(() => {
    if (!boats) return [];
    const map = new Map<number, EquipmentItem>();
    for (const boat of boats) {
      for (const g of boat.equipment) {
        map.set(g.id, g);
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [boats]);

  // The operator's own boat anchors every equipment comparison. A set can hold
  // several own boats, and this list is re-sorted by price every week — picking the
  // first would silently change the anchor as prices move, so pin it by id.
  const referenceBoat = useMemo(
    () =>
      [...(boats ?? [])].filter((b) => b.isOwnBoat).sort((a, b) => a.id.localeCompare(b.id))[0] ??
      null,
    [boats]
  );

  const sortedBoats = useMemo(() => {
    if (!boats) return [];
    const list = [...boats];

    const byPrice = (a: WeeklyComparisonBoat, b: WeeklyComparisonBoat) => {
      const priceA = getEffectivePrice(a);
      const priceB = getEffectivePrice(b);
      if (priceA === null && priceB === null) return 0;
      if (priceA === null) return 1;
      if (priceB === null) return -1;
      return priceA - priceB;
    };

    const byModelThenPrice = (a: WeeklyComparisonBoat, b: WeeklyComparisonBoat) => {
      const modelA = a.model ?? '';
      const modelB = b.model ?? '';
      if (modelA !== modelB) return modelA.localeCompare(modelB);
      return byPrice(a, b);
    };

    // Booked boats, and boats MMK stopped pricing (#334), sink to the bottom
    // regardless of mode — they're no longer useful as competitors for the
    // selected week.
    const inner = sortMode === 'price' ? byPrice : byModelThenPrice;
    const sinks = (b: WeeklyComparisonBoat) =>
      b.availabilityStatus === 'reservation' || b.lastSeenAt != null ? 1 : 0;
    list.sort((a, b) => {
      const aBooked = sinks(a);
      const bBooked = sinks(b);
      if (aBooked !== bBooked) return aBooked - bBooked;
      return inner(a, b);
    });
    return list;
  }, [boats, sortMode, getEffectivePrice]);

  const ownBoatRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const scrollTargetRef = useRef<string | null>(null);

  const handleAdjust = useCallback((boatId: string, value: number) => {
    setAdjustedDiscounts((prev) => ({ ...prev, [boatId]: Math.max(0, Math.min(100, value)) }));
    scrollTargetRef.current = boatId;
  }, []);

  useEffect(() => {
    if (!scrollTargetRef.current) return;
    const el = ownBoatRowRefs.current[scrollTargetRef.current];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    scrollTargetRef.current = null;
  }, [sortedBoats]);

  const handleConfirm = useCallback(
    (boatPid: string) => {
      const disc = adjustedDiscounts[boatPid];
      if (disc === undefined) return;
      const boat = boats?.find((b) => b.id === boatPid);
      discountMutation.mutate({ boatPid, discountPct: disc, product: boat?.product });
    },
    [adjustedDiscounts, boats, discountMutation]
  );

  const [tableOpen, setTableOpen] = useState(true);

  return (
    <Collapsible open={tableOpen} onOpenChange={setTableOpen}>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Ship className="size-5" />
                Price Comparison
              </CardTitle>
              <CardDescription>
                {compSet.status === 'syncing' ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="size-3 animate-spin" />
                    Syncing competitors...
                  </span>
                ) : (
                  `${compSet.marketBoatCount} ${compSet.marketBoatCount === 1 ? 'boat' : 'boats'} in this competitor set`
                )}
              </CardDescription>
            </div>

            <div className="flex items-center gap-3">
              {/* Year + week selectors */}
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  disabled={selectedWeekIndex <= 0 && !prevRollover}
                  onClick={() => {
                    if (selectedWeekIndex > 0) {
                      const w = calendar[selectedWeekIndex - 1];
                      if (w) onSelectWeek(w.weekStart);
                    } else if (prevRollover) {
                      onSelectWeek(prevRollover, { push: true });
                    }
                  }}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Select
                  value={String(selectedWeekIndex)}
                  onValueChange={(v) => {
                    const w = calendar[Number(v)];
                    if (w) onSelectWeek(w.weekStart);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue>{weekLabel}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {calendar.map((w, idx) => (
                      <SelectItem key={w.weekStart} value={String(idx)}>
                        {formatWeekRange(w.weekStart, w.weekEnd)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  disabled={selectedWeekIndex >= calendar.length - 1 && !nextRollover}
                  onClick={() => {
                    if (selectedWeekIndex < calendar.length - 1) {
                      const w = calendar[selectedWeekIndex + 1];
                      if (w) onSelectWeek(w.weekStart);
                    } else if (nextRollover) {
                      onSelectWeek(nextRollover, { push: true });
                    }
                  }}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>

              {/* Source toggle — MMK vs the Boataround agency mirror */}
              {tableOpen && (
                <div className="flex rounded-md border border-input">
                  <button
                    type="button"
                    aria-pressed={source === 'mmk'}
                    className={`px-3 py-1 text-xs ${source === 'mmk' ? 'bg-primary text-primary-foreground' : 'bg-background'} rounded-l-md`}
                    onClick={() => setSource('mmk')}
                  >
                    MMK
                  </button>
                  <button
                    type="button"
                    aria-pressed={source === 'boataround'}
                    className={`px-3 py-1 text-xs ${source === 'boataround' ? 'bg-primary text-primary-foreground' : 'bg-background'} rounded-r-md`}
                    onClick={() => setSource('boataround')}
                  >
                    Boataround
                  </button>
                </div>
              )}

              {/* Sort toggle */}
              {tableOpen && (
                <div className="flex rounded-md border border-input">
                  <button
                    type="button"
                    aria-pressed={sortMode === 'price'}
                    className={`px-3 py-1 text-xs ${sortMode === 'price' ? 'bg-primary text-primary-foreground' : 'bg-background'} rounded-l-md`}
                    onClick={() => setSortMode('price')}
                  >
                    By Price
                  </button>
                  <button
                    type="button"
                    aria-pressed={sortMode === 'model'}
                    className={`px-3 py-1 text-xs ${sortMode === 'model' ? 'bg-primary text-primary-foreground' : 'bg-background'} rounded-r-md`}
                    onClick={() => setSortMode('model')}
                  >
                    By Model
                  </button>
                </div>
              )}

              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  {tableOpen ? 'Hide' : 'Show'}
                  {tableOpen ? (
                    <ChevronUp className="ml-1 size-4" />
                  ) : (
                    <ChevronDown className="ml-1 size-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            {/* Treat placeholder data (stale prior week/source kept by
                keepPreviousData) as loading so we never render one source's
                rows under the other source's active toggle. */}
            {isLoading || isPlaceholderData ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : !sortedBoats || sortedBoats.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {source === 'boataround'
                  ? 'No Boataround data for this week yet. The daily sync populates your boats and the competitors priced in front of them.'
                  : 'No boats matched the filters or no pricing data available for this week.'}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Boat</TableHead>
                      <TableHead className="hidden sm:table-cell">Marina</TableHead>
                      <TableHead className="hidden text-right sm:table-cell">Comm.</TableHead>
                      <TableHead className="text-right">List Price</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Final Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedBoats.map((boat) => (
                      <SimplifiedBoatRow
                        key={boat.id}
                        ref={(el) => {
                          if (boat.isOwnBoat) {
                            ownBoatRowRefs.current[boat.id] = el;
                          }
                        }}
                        boat={boat}
                        allEquipment={allEquipment}
                        referenceBoat={referenceBoat}
                        editable={source === 'mmk'}
                        adjustedDiscount={adjustedDiscounts[boat.id]}
                        onAdjust={handleAdjust}
                        onConfirm={handleConfirm}
                        isPending={
                          discountMutation.isPending &&
                          discountMutation.variables?.boatPid === boat.id
                        }
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// ── Equipment HoverCard ──

function EquipmentTierDot({ tier }: { tier: EquipmentItem['tier'] }) {
  const tone =
    tier === 'premium'
      ? 'bg-amber-500'
      : tier === 'comfort'
        ? 'bg-sky-500'
        : tier === 'baseline'
          ? 'bg-muted-foreground/60'
          : 'bg-transparent ring-1 ring-muted-foreground/40';
  return (
    <span
      role="img"
      aria-label={tier ?? 'unrated'}
      title={tier ?? 'not scored'}
      className={`inline-block size-1.5 shrink-0 rounded-full ${tone}`}
    />
  );
}

function EquipmentList({ items, empty }: { items: EquipmentItem[]; empty: string }) {
  if (items.length === 0) {
    return <p className="text-[11px] text-muted-foreground">{empty}</p>;
  }
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.externalId} className="flex items-center gap-1.5 text-[11px]">
          <EquipmentTierDot tier={item.tier} />
          <span className="flex-1 truncate">{item.name}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {formatPoints(item.points)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Head-to-head equipment diff against the reference boat. The named items are what
 * an operator can actually act on and defend to an owner — the score only orders
 * the rows. Falls back to a plain list when there is no reference boat to compare
 * against, or when hovering the reference boat itself.
 */
function EquipmentHoverCard({
  boat,
  reference,
  allEquipment,
}: {
  boat: EquipmentComparable;
  reference: EquipmentComparable | null;
  allEquipment: EquipmentItem[];
}) {
  // Every own boat shows the plain list: a diff of your boat against your boat, or
  // against your other boat, is not the comparison anyone wants here.
  const isSelf = reference === null || boat.isOwnBoat;
  const comparison =
    isSelf || reference === null
      ? null
      : { reference, diff: buildEquipmentDiff(reference.equipment, boat.equipment) };
  const boatEquipmentIds = new Set(boat.equipment.map((g) => g.id));

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button type="button" className="rounded p-0.5 text-muted-foreground hover:text-foreground">
          <Settings2 className="size-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-80">
        {comparison ? (
          <>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <p className="text-xs font-medium">{boat.name} vs your boat</p>
              {boat.equipmentScore && comparison.reference.equipmentScore && (
                <p className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                  {formatPoints(boat.equipmentScore.points)} vs{' '}
                  {formatPoints(comparison.reference.equipmentScore.points)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div>
                <p className="mb-1 flex items-baseline justify-between text-[10px] font-medium uppercase tracking-wide text-rose-600 dark:text-rose-400">
                  <span>They have, you don&apos;t</span>
                  {comparison.diff.theyHave.length > 0 && (
                    <span className="tabular-nums">
                      +{formatPoints(sumPoints(comparison.diff.theyHave))}
                    </span>
                  )}
                </p>
                <EquipmentList items={comparison.diff.theyHave} empty="Nothing you're missing" />
              </div>
              <div>
                <p className="mb-1 flex items-baseline justify-between text-[10px] font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                  <span>You have, they don&apos;t</span>
                  {comparison.diff.youHave.length > 0 && (
                    <span className="tabular-nums">
                      +{formatPoints(sumPoints(comparison.diff.youHave))}
                    </span>
                  )}
                </p>
                <EquipmentList items={comparison.diff.youHave} empty="Nothing extra on your boat" />
              </div>
              <details>
                <summary className="cursor-pointer text-[10px] uppercase tracking-wide text-muted-foreground">
                  Both have ({comparison.diff.shared.length})
                </summary>
                <div className="mt-1">
                  <EquipmentList items={comparison.diff.shared} empty="Nothing in common" />
                </div>
              </details>
              <div className="flex items-center gap-2 border-t pt-1.5 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <EquipmentTierDot tier="premium" /> premium 7-10
                </span>
                <span className="flex items-center gap-1">
                  <EquipmentTierDot tier="comfort" /> comfort 3-6
                </span>
                <span className="flex items-center gap-1">
                  <EquipmentTierDot tier="baseline" /> baseline 1-2
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="mb-2 text-xs font-medium">Equipment</p>
            <div className="flex flex-wrap gap-1">
              {allEquipment.map((g) => (
                <Badge
                  key={g.id}
                  variant="outline"
                  className={boatEquipmentIds.has(g.id) ? '' : 'opacity-30 blur-[0.5px]'}
                >
                  {g.name}
                </Badge>
              ))}
            </div>
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

// ── Simplified Boat Row ──

function MmkBrochureButton({
  externalId,
  companyExternalId,
  boatName,
}: {
  externalId: string | null;
  companyExternalId: string | null;
  boatName: string;
}) {
  const [open, setOpen] = useState(false);

  if (!externalId || !companyExternalId) return null;

  const brochureUrl =
    `https://portal.booking-manager.com/wbm2/page.html` +
    `?view=YachtDetails&addMargins=true&templateType=responsive` +
    `&companyid=${encodeURIComponent(companyExternalId)}` +
    `&yachtId=${encodeURIComponent(externalId)}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                className="text-muted-foreground hover:text-foreground"
                aria-label={`View MMK brochure for ${boatName}`}
              >
                <ExternalLink className="size-3" />
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>View MMK brochure</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DialogContent className="flex h-[90vh] max-h-[90vh] w-[95vw] max-w-[95vw] flex-col gap-0 overflow-hidden rounded-xl p-0 sm:max-w-[95vw] sm:rounded-xl">
        <DialogHeader className="border-b px-4 py-2">
          <DialogTitle className="text-sm">{boatName} — MMK Brochure</DialogTitle>
        </DialogHeader>
        <iframe
          src={brochureUrl}
          title={`MMK brochure for ${boatName}`}
          className="w-full flex-1 border-0"
          referrerPolicy="no-referrer"
        />
      </DialogContent>
    </Dialog>
  );
}

const SimplifiedBoatRow = forwardRef<
  HTMLTableRowElement,
  {
    boat: WeeklyComparisonBoat;
    allEquipment: EquipmentItem[];
    referenceBoat: WeeklyComparisonBoat | null;
    editable: boolean;
    adjustedDiscount: number | undefined;
    onAdjust: (boatId: string, value: number) => void;
    onConfirm: (boatPid: string) => void;
    isPending: boolean;
  }
>(
  (
    {
      boat,
      allEquipment,
      referenceBoat,
      editable,
      adjustedDiscount,
      onAdjust,
      onConfirm,
      isPending,
    },
    ref
  ) => {
    const hasAdjustment =
      adjustedDiscount !== undefined && adjustedDiscount !== (boat.discountPct ?? 0);
    const newPrice =
      hasAdjustment && boat.listPrice != null
        ? Math.round(boat.listPrice * (1 - adjustedDiscount / 100))
        : null;

    const isBooked = boat.availabilityStatus === 'reservation';
    // MMK stopped returning the price while still reading the week (#334).
    const lastSeen = boat.lastSeenAt != null ? lastSeenLabel(boat.lastSeenAt) : null;

    return (
      <TableRow
        ref={ref}
        className={cn(
          boat.isOwnBoat && 'bg-green-50 dark:bg-green-950/20',
          (isBooked || lastSeen) && 'opacity-50'
        )}
      >
        {/* Boat (compact: name + model/charter) */}
        <TableCell>
          <div className="flex flex-col">
            <span className="flex items-center gap-1 text-sm font-medium">
              {boat.name}
              <MmkBrochureButton
                externalId={boat.externalId}
                companyExternalId={boat.companyExternalId}
                boatName={boat.name}
              />
              {boat.isOwnBoat && (
                <Badge variant="secondary" className="ml-1 px-1 py-0 text-[10px]">
                  yours
                </Badge>
              )}
              {isBooked && (
                <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px]">
                  Booked
                </Badge>
              )}
              {lastSeen && (
                <Badge
                  variant="outline"
                  className="ml-1 px-1 py-0 text-[10px]"
                  title="MMK stopped returning this price: it is the last one seen, not the price now"
                >
                  {lastSeen}
                </Badge>
              )}
              {allEquipment.length > 0 && (
                <EquipmentScoreBadge boat={boat} reference={referenceBoat} />
              )}
              {allEquipment.length > 0 && (
                <EquipmentHoverCard
                  boat={boat}
                  reference={referenceBoat}
                  allEquipment={allEquipment}
                />
              )}
            </span>
            <span className="text-xs text-muted-foreground">
              {[boat.model, boat.year, boat.charterName].filter(Boolean).join(' · ')}
            </span>
          </div>
        </TableCell>

        {/* Marina (hidden on mobile) */}
        <TableCell className="hidden text-xs sm:table-cell">{boat.homeBase ?? '—'}</TableCell>

        {/* Commission % (hidden on mobile) */}
        <TableCell className="hidden text-right text-xs sm:table-cell">
          {boat.commissionPct != null ? `${Number(boat.commissionPct)}%` : '—'}
        </TableCell>

        {/* List Price */}
        <TableCell className="text-right text-xs">
          <div className="flex flex-col items-end">
            <span>
              {boat.listPrice != null ? formatCurrency(boat.listPrice, boat.currency) : '—'}
            </span>
            {boat.additionalProduct?.listPrice != null && (
              <span className="text-[10px] text-muted-foreground">
                {boat.additionalProduct.product}:{' '}
                {formatCurrency(boat.additionalProduct.listPrice, boat.additionalProduct.currency)}
              </span>
            )}
          </div>
        </TableCell>

        {/* Discount — editable for own boats, static for competitors */}
        <TableCell className="text-right text-xs">
          <div className="flex flex-col items-end gap-1">
            {editable && boat.isOwnBoat && boat.listPrice != null ? (
              <div className="inline-flex items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  className="h-7 w-16 text-right text-xs"
                  value={adjustedDiscount ?? boat.discountPct ?? 0}
                  onChange={(e) => onAdjust(boat.id, Number(e.target.value))}
                />
                <span className="text-xs text-muted-foreground">%</span>
                <div className="flex flex-col">
                  <button
                    type="button"
                    className="rounded p-0.5 hover:bg-muted"
                    onClick={() =>
                      onAdjust(boat.id, (adjustedDiscount ?? boat.discountPct ?? 0) + 1)
                    }
                  >
                    <ChevronUp className="size-3" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-0.5 hover:bg-muted"
                    onClick={() =>
                      onAdjust(boat.id, (adjustedDiscount ?? boat.discountPct ?? 0) - 1)
                    }
                  >
                    <ChevronDown className="size-3" />
                  </button>
                </div>
                {hasAdjustment && (
                  <Button
                    variant="default"
                    size="sm"
                    className="ml-1 h-7 px-2 text-xs"
                    disabled={isPending}
                    onClick={() => onConfirm(boat.id)}
                  >
                    {isPending ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <Check className="size-3" />
                    )}
                  </Button>
                )}
              </div>
            ) : (
              <span>{boat.discountPct != null ? `${boat.discountPct}%` : '—'}</span>
            )}
            {boat.additionalProduct && (
              <span className="text-[10px] text-muted-foreground">
                {boat.additionalProduct.product}:{' '}
                {boat.additionalProduct.discountPct != null
                  ? `${boat.additionalProduct.discountPct}%`
                  : '—'}
              </span>
            )}
          </div>
        </TableCell>

        {/* Final Price */}
        <TableCell className="text-right text-sm font-medium">
          <div className="flex flex-col items-end">
            <span>
              {newPrice != null ? (
                <span className="text-green-600 dark:text-green-400">
                  {formatCurrency(newPrice, boat.currency)}
                </span>
              ) : boat.priceAfterDiscount != null ? (
                formatCurrency(boat.priceAfterDiscount, boat.currency)
              ) : (
                '—'
              )}
            </span>
            {boat.additionalProduct?.priceAfterDiscount != null && (
              <span className="text-[10px] font-normal text-muted-foreground">
                {boat.additionalProduct.product}:{' '}
                {formatCurrency(
                  boat.additionalProduct.priceAfterDiscount,
                  boat.additionalProduct.currency
                )}
                {boat.additionalProduct.lastSeenAt != null &&
                  ` · ${lastSeenLabel(boat.additionalProduct.lastSeenAt).toLowerCase()}`}
              </span>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  }
);
SimplifiedBoatRow.displayName = 'SimplifiedBoatRow';

// ── Collapsible Side-by-Side ──

/** When MMK last returned a price it has since stopped returning (#334). */
const SEEN_DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const lastSeenLabel = (iso: string) => `Last seen ${SEEN_DATE.format(new Date(iso))}`;

const OCCUPANCY_COLORS: Record<string, { dot: string; bg: string; label: string }> = {
  reservation: {
    dot: 'bg-red-500',
    bg: 'bg-red-50/40 dark:bg-red-950/15',
    label: 'Reservation',
  },
  option: {
    dot: 'bg-orange-400',
    bg: 'bg-orange-50/40 dark:bg-orange-950/15',
    label: 'Option',
  },
  // MMK status 3: the option's expiry date has passed, so it no longer holds the boat.
  option_expired: {
    dot: 'bg-amber-500',
    bg: 'bg-amber-50/40 dark:bg-amber-950/15',
    label: 'Option expired',
  },
  service: {
    dot: 'bg-gray-400',
    bg: 'bg-gray-50/40 dark:bg-gray-950/15',
    label: 'Service',
  },
  sleepaboard: {
    dot: 'bg-blue-400',
    bg: 'bg-blue-50/40 dark:bg-blue-950/15',
    label: 'Sleep aboard',
  },
  unavailable: {
    dot: 'bg-slate-400',
    bg: 'bg-slate-50/40 dark:bg-slate-950/15',
    label: 'Unavailable',
  },
};

function formatWeekLabel(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const month = s.toLocaleDateString('en', { month: 'short' });
  const startDay = s.getDate();
  const endDay = e.getDate();
  return `${month} ${startDay}-${endDay}`;
}

function CollapsibleSideBySide({
  compSet,
  year,
  open: openProp,
  onOpenChange,
}: {
  compSet: CompSet;
  year: number;
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
}) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp ?? openInternal;
  const setOpen = onOpenChange ?? setOpenInternal;
  const [pinnedBoatIds, setPinnedBoatIds] = useState<Set<string>>(new Set());

  const togglePin = useCallback((boatId: string) => {
    setPinnedBoatIds((prev) => {
      const next = new Set(prev);
      if (next.has(boatId)) {
        next.delete(boatId);
      } else {
        next.add(boatId);
      }
      return next;
    });
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.compSets.sideBySide(compSet.id, year),
    queryFn: () => compSetsApi.sideBySide(compSet.id, { year }),
    enabled: open,
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerCellRefs = useRef<Record<number, HTMLTableCellElement | null>>({});
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const [headerHeight, setHeaderHeight] = useState(37);

  // Measure header row height for pinned row offset
  useEffect(() => {
    if (headerRowRef.current) {
      setHeaderHeight(headerRowRef.current.getBoundingClientRect().height);
    }
  }, [data]);

  // Auto-scroll to current week on data load
  useEffect(() => {
    if (!data || !scrollContainerRef.current) return;
    const today = new Date().toISOString().split('T')[0]!;
    const currentWeekIdx = data.weeks.findIndex((w) => w.weekStart <= today && w.weekEnd > today);
    const targetIdx = currentWeekIdx >= 0 ? currentWeekIdx : 0;
    const cell = headerCellRefs.current[targetIdx];
    if (cell) {
      cell.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [data]);

  // Get own boat prices for color coding
  const ownBoatPrices = useMemo(() => {
    if (!data) return [];
    const ownBoat = data.boats.find((b) => b.isOwnBoat);
    if (!ownBoat) return [];
    // A price MMK stopped returning (#334) is no basis for comparison.
    return ownBoat.weeks.map((w) => (w.lastSeenAt != null ? null : w.price));
  }, [data]);

  const allEquipmentSideBySide = useMemo(() => {
    if (!data) return [];
    const map = new Map<number, EquipmentItem>();
    for (const boat of data.boats) {
      for (const g of boat.equipment) {
        map.set(g.id, g);
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // Same anchor rule as the weekly view, so both agree on "your boat".
  const referenceBoatSideBySide = useMemo(
    () =>
      [...(data?.boats ?? [])]
        .filter((b) => b.isOwnBoat)
        .sort((a, b) => a.id.localeCompare(b.id))[0] ?? null,
    [data]
  );

  // Split boats into pinned (sticky top) and unpinned groups
  const { pinnedBoats, unpinnedBoats } = useMemo(() => {
    if (!data) return { pinnedBoats: [], unpinnedBoats: [] };
    const pinned = data.boats.filter((b) => pinnedBoatIds.has(b.id));
    const unpinned = data.boats.filter((b) => !pinnedBoatIds.has(b.id));
    return { pinnedBoats: pinned, unpinnedBoats: unpinned };
  }, [data, pinnedBoatIds]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="flex h-[90vh] max-h-[90vh] w-[90vw] max-w-[90vw] flex-col gap-0 overflow-hidden rounded-xl p-0 sm:max-w-[90vw] sm:rounded-xl">
        <DialogHeader className="space-y-1 border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Grid3X3 className="size-5" />
            Full Season Matrix · {year}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            {data ? (
              <>
                <span>
                  {data.boats.length} boats &times; {data.weeks.length} weeks
                </span>
                <span className="flex items-center gap-3">
                  {Object.entries(OCCUPANCY_COLORS).map(([key, val]) => (
                    <span key={key} className="inline-flex items-center gap-1">
                      <span className={`inline-block size-2 rounded-full ${val.dot}`} />
                      {val.label}
                    </span>
                  ))}
                </span>
              </>
            ) : (
              <span>Per-boat pricing for every charter week</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-hidden">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !data || data.boats.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-muted-foreground">
              No boats or pricing data available for {year}.
            </p>
          ) : (
            <TooltipProvider delayDuration={300}>
              {/* Raw <table> instead of <Table> — the shadcn Table component
                  wraps in a <div class="overflow-auto"> which creates a new
                  scroll context and breaks position:sticky on cells. */}
              <div
                ref={scrollContainerRef}
                className="scrollbar-none h-full overflow-x-auto overflow-y-auto"
              >
                <table className="w-full caption-bottom text-sm">
                  <TableHeader className="sticky top-0 z-30">
                    <TableRow ref={headerRowRef}>
                      <TableHead className="sticky left-0 z-40 min-w-[200px] border-r bg-background">
                        Boat
                      </TableHead>
                      {data.weeks.map((w, idx) => (
                        <TableHead
                          key={w.weekStart}
                          ref={(el) => {
                            headerCellRefs.current[idx] = el;
                          }}
                          className="min-w-[90px] whitespace-nowrap bg-background px-2 text-center text-xs"
                        >
                          {formatWeekLabel(w.weekStart, w.weekEnd)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {/* Pinned rows — each cell is sticky so the row stays visible */}
                    {pinnedBoats.map((boat, i) => (
                      <MatrixBoatRow
                        key={boat.id}
                        boat={boat}
                        isPinned
                        isLastPinned={i === pinnedBoats.length - 1}
                        stickyTop={headerHeight}
                        pinnedIndex={i}
                        ownBoatPrices={ownBoatPrices}
                        allEquipment={allEquipmentSideBySide}
                        referenceBoat={referenceBoatSideBySide}
                        onTogglePin={togglePin}
                      />
                    ))}

                    {/* Unpinned rows — normal scroll */}
                    {unpinnedBoats.map((boat) => (
                      <MatrixBoatRow
                        key={boat.id}
                        boat={boat}
                        isPinned={false}
                        isLastPinned={false}
                        stickyTop={0}
                        pinnedIndex={0}
                        ownBoatPrices={ownBoatPrices}
                        allEquipment={allEquipmentSideBySide}
                        referenceBoat={referenceBoatSideBySide}
                        onTogglePin={togglePin}
                      />
                    ))}
                  </TableBody>
                </table>
              </div>
            </TooltipProvider>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Row height estimate for stacking multiple pinned rows
const PINNED_ROW_HEIGHT = 49;

function MatrixBoatRow({
  boat,
  isPinned,
  isLastPinned,
  stickyTop,
  pinnedIndex,
  ownBoatPrices,
  allEquipment,
  referenceBoat,
  onTogglePin,
}: {
  boat: SideBySideBoatRow;
  isPinned: boolean;
  isLastPinned: boolean;
  stickyTop: number;
  pinnedIndex: number;
  ownBoatPrices: (number | null)[];
  allEquipment: EquipmentItem[];
  referenceBoat: SideBySideBoatRow | null;
  onTogglePin: (boatId: string) => void;
}) {
  // Subtract 1px so pinned cells overlap the header's border-bottom (no visible gap)
  const computedTop = isPinned ? stickyTop + pinnedIndex * PINNED_ROW_HEIGHT - 1 : undefined;

  const cellBg = boat.isOwnBoat
    ? 'bg-blue-50 dark:bg-blue-950/20'
    : isPinned
      ? 'bg-muted'
      : 'bg-background';

  return (
    <TableRow
      className={`group ${isLastPinned ? 'border-b-2 border-border shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1)]' : ''}`}
    >
      <TableCell
        className={`min-w-[200px] border-r ${cellBg} ${isPinned ? 'sticky left-0 z-20' : 'sticky left-0 z-10'}`}
        style={isPinned ? { position: 'sticky', top: computedTop, left: 0, zIndex: 25 } : undefined}
      >
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onTogglePin(boat.id)}
                className={`shrink-0 rounded p-0.5 transition-colors hover:bg-accent ${
                  isPinned ? 'text-primary' : 'text-muted-foreground/40 hover:text-muted-foreground'
                }`}
                aria-label={isPinned ? 'Unpin row' : 'Pin row'}
                aria-pressed={isPinned}
              >
                <Pin className={`size-3.5 ${isPinned ? 'fill-current' : ''}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">
              {isPinned ? 'Unpin row' : 'Pin to top'}
            </TooltipContent>
          </Tooltip>
          <div className="flex min-w-0 flex-col">
            <span className="flex items-center gap-1 max-w-[160px] text-sm font-medium">
              <span className="truncate">{boat.name}</span>
              {boat.isOwnBoat && (
                <Badge variant="secondary" className="shrink-0 px-1 py-0 text-[10px]">
                  yours
                </Badge>
              )}
              {allEquipment.length > 0 && (
                <EquipmentScoreBadge boat={boat} reference={referenceBoat} />
              )}
              {allEquipment.length > 0 && (
                <EquipmentHoverCard
                  boat={boat}
                  reference={referenceBoat}
                  allEquipment={allEquipment}
                />
              )}
            </span>
            <span className="max-w-[160px] truncate text-xs text-muted-foreground">
              {boat.charterName ?? boat.model ?? ''}
            </span>
          </div>
        </div>
      </TableCell>
      {boat.weeks.map((weekPrice, idx) => (
        <PriceCell
          key={weekPrice.weekStart}
          weekPrice={weekPrice}
          ownPrice={ownBoatPrices[idx] ?? null}
          isOwnBoat={boat.isOwnBoat}
          isPinned={isPinned}
          stickyTop={computedTop}
          cellBg={cellBg}
        />
      ))}
    </TableRow>
  );
}

function PriceCell({
  weekPrice,
  ownPrice,
  isOwnBoat,
  isPinned,
  stickyTop,
  cellBg,
}: {
  weekPrice: SideBySideWeekPrice;
  ownPrice: number | null;
  isOwnBoat: boolean;
  isPinned: boolean;
  stickyTop?: number;
  cellBg?: string;
}) {
  const occupancy = weekPrice.occupancyStatus ? OCCUPANCY_COLORS[weekPrice.occupancyStatus] : null;
  const stickyStyle =
    stickyTop !== undefined
      ? { position: 'sticky' as const, top: stickyTop, zIndex: 20 }
      : undefined;

  // Solid backdrop on pinned rows so scrolled-through cells don't bleed
  // through the alpha-blended occupancy/diff overlays.
  const backdrop = isPinned && (
    <span
      aria-hidden
      className={`pointer-events-none absolute inset-0 ${cellBg ?? 'bg-background'}`}
    />
  );

  if (weekPrice.price === null) {
    return (
      <TableCell
        className={`relative px-2 py-1.5 text-center text-xs text-muted-foreground ${cellBg ?? ''} ${occupancy?.bg ?? ''}`}
        title={occupancy?.label}
        style={stickyStyle}
      >
        {backdrop}
        {occupancy && (
          <span className={`absolute right-0.5 top-0.5 size-1.5 rounded-full ${occupancy.dot}`} />
        )}
        <span className="relative">--</span>
      </TableCell>
    );
  }

  // MMK stopped returning this price (#334): shown muted, never compared.
  if (weekPrice.lastSeenAt != null) {
    return (
      <TableCell
        className={`relative px-2 py-1.5 text-center font-mono text-xs italic text-muted-foreground ${cellBg ?? ''} ${occupancy?.bg ?? ''}`}
        title={`${lastSeenLabel(weekPrice.lastSeenAt)}: MMK stopped returning this price`}
        style={stickyStyle}
      >
        {backdrop}
        {occupancy && (
          <span className={`absolute right-0.5 top-0.5 size-1.5 rounded-full ${occupancy.dot}`} />
        )}
        <span className="relative">{formatCurrency(weekPrice.price, weekPrice.currency)}</span>
      </TableCell>
    );
  }

  let colorClass = '';
  if (!isOwnBoat && ownPrice !== null) {
    const diff = weekPrice.price - ownPrice;
    const pct = (diff / ownPrice) * 100;
    if (pct > 5) {
      colorClass = 'text-green-700 bg-green-50/50 dark:text-green-400 dark:bg-green-950/20';
    } else if (pct < -5) {
      colorClass = 'text-red-700 bg-red-50/50 dark:text-red-400 dark:bg-red-950/20';
    } else {
      colorClass = 'text-amber-700 dark:text-amber-400';
    }
  }

  return (
    <TableCell
      className={`relative px-2 py-1.5 text-center font-mono text-xs ${colorClass} ${cellBg ?? ''} ${occupancy?.bg ?? ''}`}
      title={occupancy?.label}
      style={stickyStyle}
    >
      {backdrop}
      {occupancy && (
        <span className={`absolute right-0.5 top-0.5 size-1.5 rounded-full ${occupancy.dot}`} />
      )}
      <span className="relative">{formatCurrency(weekPrice.price, weekPrice.currency)}</span>
    </TableCell>
  );
}
